// name: LLM_analysis_functions.js
// folderstructure: my-app/src/functions
// This file contains functions for fetching and posting investment strategies
import store from '../redux/store';


import api from './api';
import getUserId from '../functions/getUserId';
import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, Typography, Button, Box, Collapse } from '@mui/material';

// === Token Estimator ===
export const estimateTokenCount = (text) => {
  if (!text) return 0;
  return Math.ceil(text.length * 0.3); // modern LLM: ~0.3 token per char
};


// === Token Limit Checker ===
// === Token Limits ===
const LLM_TOKEN_LIMITS = {
  gemini: 30720,
  'deepseek-chat': 16384,
  'deepseek-reasoner': 28000,
  default: 32000
};

export const checkTokenLimit = (text, llmModel) => {
  const count = estimateTokenCount(text);
  const limit = LLM_TOKEN_LIMITS[llmModel] || LLM_TOKEN_LIMITS.default;
  if (count > limit) {
    return `⚠️ Estimated ${count} tokens exceeds ${limit} token limit for ${llmModel}`;
  }
  return null;
};


// === Safe LLM Caller ===
export const callLLM = async (promptText, llmModel) => {
  const estimatedTokens = estimateTokenCount(promptText);
  const limit = LLM_TOKEN_LIMITS[llmModel] || LLM_TOKEN_LIMITS.default;

  // console.log(`🔑 Requested LLM model: ${llmModel}`);
  // console.log(`🧮 Estimated input tokens: ${estimatedTokens}`);

  if (estimatedTokens > limit) {
    const warning = `⚠️ Estimated ${estimatedTokens} tokens exceeds ${limit} token limit for ${llmModel}`;
    console.warn(warning);
    return warning;
  }

  try {
    const response = await api.post('/investment_strategy', {
      prompt: promptText,
      llm_model: llmModel
    });

    if (response.data?.error) {
      return `⚠️ ERROR: ${response.data.error}`;
    }

    if (response.data?.gemini_response) return response.data.gemini_response;
    if (response.data?.deepseek_response) return response.data.deepseek_response;

    return '⚠️ LLM call returned no result';
  } catch (error) {
    console.error(`❌ ${llmModel} call failed:`, error);
    return `❌ ${llmModel} call failed: ${error?.message || error.toString()}`;
  }
};


export const fetchLLMContextFields = async () => {
  // console.log('Fetching LLM context fields...');
  try {
    const response = await api.get('/llm_context_fields');
    // console.log('LLM context fields response:', response.data);
    const userFields = response.data?.userspecificcontext_fields || [];
    const contextFields = response.data?.context_fields || [];
    return [...userFields, ...contextFields];
  } catch (error) {
    console.error('Error fetching LLM context fields:', error);
    return [];
  }
};

export const fetchContextField = async (field) => {
  const userid = getUserId();
  try {
    const response = await api.get('/investment_strategy', {
      params: { userid, context: field }
    });
    console.log(`Fetched [${field}]:`, response.data);
    return response.data?.[field] || '';
  } catch (error) {
    console.error(`Error fetching ${field}:`, error);
    return '';
  }
};

// === Question Type Selector ===
export const QuestionTypeSelector = ({ questionType, setQuestionType }) => (
  <FormControl size="small" sx={{ mb: 3, width: '100%' }}>
    <InputLabel id="question-type-label">Question Type</InputLabel>
    <Select
      labelId="question-type-label"
      id="question-type-select"
      value={questionType}
      label="Question Type"
      onChange={(e) => setQuestionType(e.target.value)}
    >
      <MenuItem value="Generic Advice">Generic Advice</MenuItem>
      <MenuItem value="Specific stock - General">Specific stock - General</MenuItem>
      <MenuItem value="Specific stock - My Portfolio">Specific stock - My Portfolio</MenuItem>
      <MenuItem value="Specific Portfolio Alert">Specific Portfolio Alert</MenuItem>
      <MenuItem value="Specific Favorites Alert">Specific Favorites Alert</MenuItem>
      <MenuItem value="Specific Opportunity Alert">Specific Opportunity Alert</MenuItem>
    </Select>
  </FormControl>
);

// === Contexts Selector ===
export const ContextsSelector = ({
  showContexts,
  setShowContexts,
  contextOptions,
  availableContexts,
  contextSelections,
  handleContextToggle,
  handlePreviewContext
}) => (
  <>
    <Typography
      variant="subtitle1"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
      onClick={() => setShowContexts(prev => !prev)}
    >
      Contexts
      <Button size="small">{showContexts ? "Hide" : "Show"}</Button>
    </Typography>

    <Collapse in={showContexts}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {contextOptions.map((item) => {
          const isDisabled = !availableContexts[item];
          const isSelected = contextSelections[item];
          return (
            <Box
              key={item}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Button
                variant={isSelected ? "contained" : "outlined"}
                size="small"
                disabled={isDisabled}
                onClick={() => !isDisabled && handleContextToggle(item)}
                sx={{
                  flexGrow: 1,
                  justifyContent: 'flex-start',
                  fontSize: '0.75rem',
                  opacity: isDisabled ? 0.5 : 1,
                }}
              >
                {item}
              </Button>

              <Button
                size="small"
                disabled={isDisabled}
                onClick={() => !isDisabled && handlePreviewContext(item)}
                sx={{ minWidth: '32px' }}
              >
                🔍
              </Button>
            </Box>
          );
        })}
      </Box>
    </Collapse>
  </>
);

// === Get PF_ALERT data from Redux ===

export const getPFAlertData = () => {
  return store.getState()?.portfolio?.data?.pf_alert || [];
};

export const getFavoritesAlertData = () => {
  return store.getState()?.favorites?.data?.favorites_alert_data || [];
};

export const getOpportunitiesAlertData = () => {
  return store.getState()?.portfolio?.data?.opportunities_alerts || [];
};

export const getPortfolioData = () => {
  return store.getState()?.portfolio?.data?.portfolio || [];
};

export const formatFundamentalFieldsForPrompt = (fields) => {
  if (!fields || !Array.isArray(fields)) return "";

  return fields
    .filter(f => f?.label && f?.value !== undefined && f.label !== 'ID' && f.label !== 'ticker')
    .map(f => {
      const label = f.label;
      const value = f.value + (f.showBehind || '');
      const trend = f.quarterTrend || "N/A";
      const ranking = f.sectorRanking !== null && f.sectorRanking !== undefined
        ? `Rank: ${f.sectorRanking}`
        : null;
      const toAvg = f.toAverage !== null && f.toAverage !== undefined
        ? `vs Avg: ${f.toAverage}%`
        : null;

      const trendSymbol = trend.toLowerCase().includes("positive") ? "🔼" :
                          trend.toLowerCase().includes("negative") ? "🔻" : "";

      const line = `${label}: ${value} ${trendSymbol}`;
      const details = [trend !== "N/A" ? `Trend: ${trend}` : null, ranking, toAvg]
        .filter(Boolean)
        .join(" | ");

      return `${line}\n${details}`;
    })
    .join("\n\n");
};

export const formatTablesDataForPrompt = (tablesData) => {
  if (!Array.isArray(tablesData)) return "";

  return tablesData.map(table => {
    const { table_name, data } = table;
    if (!data || data.length !== 2) return "";

    const fields = data[0];
    const values = data[1];

    const lines = fields.map((field, index) => {
      let value = Array.isArray(values[index]) ? values[index][0] : values[index]; // unwrap array if needed
      return `${field}: ${value}`;
    });

    return `--- ${table_name.replace(/_/g, ' ').toUpperCase()} ---\n` + lines.join("\n");
  }).join("\n\n");
};

// name: LLM_analysis_functions.js

export function formatPortfolioPositionForPrompt(data) {
  if (!data || typeof data !== 'object') return '';

  const safe = (val) => (val === null || val === undefined || val === 'null' ? '–' : val);

  return [
    `Ticker: ${safe(data.Ticker)}`,
    `Company: ${safe(data.Company)}`,
    `Account: ${safe(data.account)}`,
    `Category: ${safe(data.Category)}`,
    `Asset Type: ${safe(data.AssetType)}`,
    `Buy Price: ${safe(data.AvgPrice)}`,
    `Value (EUR): ${safe(data.Value_eur)}`,
    `In Portfolio Since: ${safe(data.in_portfolio_since || data.latest_tx_date)}`,
    `Unrealized P/L: ${safe(data.unrealized_prof_loss_Eur)} EUR (${safe(data.unrealized_prof_loss_Eur_perc)}%)`,
    `Realized Result: ${safe(data.realized_Eur)} EUR`,
    `Realized Performance: ${safe(data.realized_results_current_positions_performance)}%`,
    `YTD Performance: ${safe(data.performance_ytd)}%`,
    `Performance Since Last Tx: ${safe(data.performance_since_last_tx)}%`,
    `Relative Performance: ${safe(data.Overall_Relative_Performance)}%`,
    `Sharpe Ratio: ${safe(data.Overall_Relative_Sharpe_Ratio_Performance)}%`,
    `Annualized Block Performance: ${safe(data.AnnualizedPerformance)}%`,
    `Last Price: ${safe(data.Last_Price)} ${safe(data.currency)}`,
    `Benchmark: ${safe(data.benchmark)}`,
  ].join('\n');
}
